// src/components/FinalCTA.js
import React, { useState, useCallback, useEffect } from 'react';
import { IoArrowUp } from 'react-icons/io5';
import { submitEmail } from '../services/apiService'; // Make sure this is correctly imported

// A simple but effective email validation regex
const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const FinalCTA = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [error, setError] = useState('');

  // Reset status after a message is shown
  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const timer = setTimeout(() => {
        setStatus('idle');
        setError('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleChange = useCallback((e) => {
    // Clear error as user types
    if (error) setError('');
    setEmail(e.target.value);
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault(); // Prevent page reload
    
    // --- Validation ---
    if (!email) {
      setError('Email address is required.');
      return;
    }
    if (!isEmailValid(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // --- Submission ---
    setStatus('loading');
    setError('');
    try {
      await submitEmail(email);
      setStatus('success');
      setEmail(''); // Clear input on success
    } catch (err) {
      // ✅ GRACEFUL ERROR HANDLING
      setStatus('error');
      if (err.status === 409) {
        // Handle the specific "Conflict" error for existing emails
        setError("This email is already on our list. Thanks for your interest!");
      } else {
        // Handle all other errors
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
  }, [email]);

  const getButtonText = () => {
    switch (status) {
      case 'loading':
        return 'Submitting...';
      case 'success':
        return 'Success!';
      default:
        return <IoArrowUp size={20} className="text-white" />;
    }
  };

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/80 to-dark-bg"></div>
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-dark-bg"></div>
      
      <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-4xl font-extrabold tracking-tight text-dark-text-primary sm:text-5xl">
              Ready to build your social empire?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-dark-text-secondary">
              Start your 14-day free trial today. No credit card required. Cancel anytime.
          </p>

          <form onSubmit={handleSubmit} className="relative w-full max-w-xl mx-auto mt-12">
              <div className="flex items-center w-full bg-black/30 backdrop-blur-lg border border-white/10 rounded-2xl shadow-2xl p-2">
                  <input
                      type="email"
                      value={email}
                      onChange={handleChange}
                      placeholder="Enter your email to get started..."
                      className="w-full bg-transparent text-dark-text-primary placeholder:text-dark-text-secondary/50 border-none outline-none px-4 text-md"
                      disabled={status === 'loading'}
                  />
                  <button 
                      type="submit"
                      disabled={status === 'loading'}
                      className={`flex-shrink-0 w-auto h-10 flex items-center justify-center rounded-full transition-all duration-300 px-4
                        ${status === 'success' ? 'bg-green-500' : 'bg-dark-accent-start hover:opacity-90'}
                        ${status === 'loading' ? 'cursor-not-allowed' : ''}
                      `}
                  >
                      {getButtonText()}
                  </button>
              </div>
              {/* Validation and Success/Error Messages */}
              <div className="absolute left-1/2 -translate-x-1/2 mt-3 text-sm">
                {status === 'error' && <p className="text-red-400">{error}</p>}
                {status === 'success' && <p className="text-green-400">Thanks! Check your inbox for next steps.</p>}
              </div>
          </form>
      </div>
    </section>
  );
};

export default FinalCTA;